import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {UserModel} from '../../auth'
import {IOfferPerformance} from '../../offers/cors/_models'
import {getOfferPerformance} from '../../offers/cors/_requests'

/**
 * "Win Rate" dashboard card — surfaces /partner-portal/offers/performance.
 * Single number for Phase 1 (winning_offers / active_offers, %), with
 * a soft subtitle and a deep-link to the My Offers page filtered to
 * status=active&winning_only=1. Charts come in Phase 3.
 *
 * Mirrors the visual conventions of the existing dashboard cards
 * (ActiveProductsCard, etc.) — colored card, large headline, small
 * descriptive text + CTA.
 */

type Props = {
  className: string
  language: string
  currentUser: UserModel | undefined
}

const WinRateCard: FC<Props> = ({className, language, currentUser}) => {
  const {formatMessage} = useIntl()
  const [perf, setPerf] = useState<IOfferPerformance | null>(null)
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!currentUser?.token) return
      try {
        const res = await getOfferPerformance(currentUser.token, language)
        if (!cancelled) {
          setPerf(res.data?.data ?? null)
          setErrored(false)
        }
      } catch {
        if (!cancelled) {
          setPerf(null)
          setErrored(true)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [language, currentUser])

  // Don't render the card if the supplier has no offers yet — keeps
  // the dashboard clean for legacy single-supplier products in transition.
  if (!perf || perf.active_offers === 0) {
    if (errored) return null
    return null
  }

  const rate = Number.isFinite(perf.winning_offer_rate) ? perf.winning_offer_rate : 0
  const reliability = perf.reliability_score
  const showReliability = reliability > 0

  return (
    <div className={`hsb-hero ${className}`}>
      <div className="hsb-hero__body">
        <div>
          <div className="d-flex justify-content-between align-items-start mb-6">
            <div>
              <h3 className="hsb-hero__eyebrow">
                {formatMessage({id: 'dashboard.win_rate.title'})}
              </h3>
              <div className="d-flex align-items-end gap-3 mt-2">
                <span className="hsb-hero__value">{rate.toFixed(1)}%</span>
                {showReliability ? (
                  <span
                    className="hsb-badge-glass"
                    title={formatMessage({id: 'dashboard.win_rate.reliability_tooltip'})}
                  >
                    {formatMessage(
                      {id: 'dashboard.win_rate.reliability_badge'},
                      {score: reliability},
                    )}
                  </span>
                ) : null}
              </div>
            </div>
            <span className="material-symbols-outlined" style={{fontSize: '48px', opacity: 0.2}}>
              rocket_launch
            </span>
          </div>
          <p className="hsb-hero__desc">
            {formatMessage(
              {id: 'dashboard.win_rate.subtitle'},
              {winning: perf.winning_offers, active: perf.active_offers},
            )}
          </p>
        </div>
        <Link to="/offers/list?status=active&winning_only=1" className="hsb-btn-white">
          {formatMessage({id: 'dashboard.win_rate.cta'})}
          <span className="material-symbols-outlined">trending_flat</span>
        </Link>
      </div>
      <div className="hsb-hero__deco" />
    </div>
  )
}

export default WinRateCard
